/**
 * NotificationsPanel — Telegram notification settings for the current user.
 *
 * Renders a connection-state-driven panel:
 *   - Connected: shows masked chat_id + Disconnect button
 *   - Not connected: shows "Connect Telegram" deep-link button with polling,
 *     plus an "Advanced — enter chat ID manually" collapsible fallback.
 *
 * Used in SettingsPage.
 */

import { useState, useEffect } from 'react';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from '../../hooks/useNotificationSettings';
import { useTelegramLink } from '../../hooks/useTelegramLink';

// ============================================================================
// Constants
// ============================================================================

/** Client-side validation mirror of the server-side regex. */
const TELEGRAM_CHAT_ID_REGEX = /^-?\d{6,15}$/;

/** Number of trailing digits to show in the masked chat_id. */
const MASKED_DIGITS = 4;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Masks a chat_id, showing only the last MASKED_DIGITS characters.
 * e.g. "123456789" → "•••••6789"
 */
function maskChatId(chatId: string): string {
  if (chatId.length <= MASKED_DIGITS) return chatId;
  const visible = chatId.slice(-MASKED_DIGITS);
  const hidden = '•'.repeat(chatId.length - MASKED_DIGITS);
  return hidden + visible;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationsPanel() {
  const { data, isLoading, isError } = useNotificationSettings();
  const { mutateAsync, isPending: isMutating } = useUpdateNotificationSettings();

  const telegramLink = useTelegramLink();

  // Manual fallback state
  const [showManual, setShowManual] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const telegramChatId = data?.telegramChatId ?? null;
  const isConnected = telegramChatId !== null;

  // Sync manual input with fetched data
  useEffect(() => {
    if (data !== undefined) {
      setInputValue(data.telegramChatId ?? '');
    }
  }, [data]);

  function validate(value: string): string | null {
    if (value === '') return null; // empty = clear, valid
    if (!TELEGRAM_CHAT_ID_REGEX.test(value)) {
      return 'Must be a signed integer with 6–15 digits (e.g. 123456789).';
    }
    return null;
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setInputValue(value);
    setValidationError(validate(value));
    setSaveStatus('idle');
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const error = validate(inputValue);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await mutateAsync({ telegramChatId: inputValue === '' ? null : inputValue });
      setSaveStatus('success');
    } catch {
      setSaveStatus('error');
    }
  }

  async function handleDisconnect() {
    try {
      await mutateAsync({ telegramChatId: null });
    } catch {
      // Disconnect failure is surfaced by saveStatus in the manual section
      // if needed; for now we silently ignore — the settings will re-fetch.
    }
  }

  async function handleConnectClick() {
    await telegramLink.createLink();
  }

  async function handleTryAgain() {
    telegramLink.reset();
    await telegramLink.createLink();
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
      <h2 className="text-base font-semibold text-white mb-1">Telegram notifications</h2>
      <p className="text-sm text-gray-400 mb-4">
        Get notified when background jobs (grid search, walk-forward, etc.) complete.
      </p>

      {/* Loading */}
      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}

      {/* Error loading settings */}
      {isError && !isLoading && (
        <p className="text-sm text-red-400">Failed to load notification settings.</p>
      )}

      {!isLoading && (
        <>
          {/* ---------------------------------------------------------------- */}
          {/* Connected state                                                   */}
          {/* ---------------------------------------------------------------- */}
          {isConnected && (
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center gap-2 text-sm text-green-400 font-medium">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Telegram connected
              </span>
              <span className="text-xs text-gray-500 font-mono">
                ({maskChatId(telegramChatId)})
              </span>
              <button
                type="button"
                onClick={() => void handleDisconnect()}
                disabled={isMutating}
                className="ml-auto px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm rounded transition-colors"
              >
                {isMutating ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Not-connected state                                               */}
          {/* ---------------------------------------------------------------- */}
          {!isConnected && (
            <div className="mb-4 space-y-3">
              {/* Primary connect button */}
              {telegramLink.status === 'idle' && (
                <button
                  type="button"
                  onClick={() => void handleConnectClick()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-2.115 9.967c-.16.717-.576.893-1.168.554l-3.232-2.38-1.56 1.5c-.172.172-.316.316-.647.316l.232-3.285 5.98-5.4c.26-.232-.056-.36-.403-.128l-7.387 4.65-3.18-1c-.692-.216-.706-.692.144-.924l12.404-4.787c.576-.208 1.08.14.932.817z" />
                  </svg>
                  Connect Telegram
                </button>
              )}

              {/* Waiting / polling state */}
              {telegramLink.status === 'waiting' && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-blue-300 animate-pulse">
                    Waiting for you to press Start in Telegram…
                  </span>
                  <button
                    type="button"
                    onClick={telegramLink.cancelPolling}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Timeout state */}
              {telegramLink.status === 'timeout' && (
                <div className="space-y-2">
                  <p className="text-sm text-yellow-400">
                    Timed out waiting for Telegram connection. Did you press Start in the bot?
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleTryAgain()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    Try again
                  </button>
                </div>
              )}

              {/* Error state */}
              {telegramLink.status === 'error' && telegramLink.error !== null && (
                <div className="space-y-2">
                  <p className="text-sm text-red-400">{telegramLink.error}</p>
                  <button
                    type="button"
                    onClick={() => void handleTryAgain()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Advanced: manual chat_id entry (collapsible)                     */}
          {/* ---------------------------------------------------------------- */}
          {!isConnected && (
            <div className="border-t border-gray-700 pt-3 mt-3">
              <button
                type="button"
                onClick={() => setShowManual((v) => !v)}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showManual ? '▲' : '▾'}{' '}
                Advanced — enter chat ID manually
              </button>

              {showManual && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">
                    You must have already pressed <strong>/start</strong> in the bot for this to
                    work. Recommended path: use the Connect button above.
                  </p>
                  <form onSubmit={(e) => void handleSave(e)} className="space-y-3">
                    <div>
                      <label
                        htmlFor="telegram-chat-id"
                        className="block text-sm font-medium text-gray-300 mb-1"
                      >
                        Telegram chat ID
                      </label>
                      <input
                        id="telegram-chat-id"
                        type="text"
                        value={inputValue}
                        onChange={handleChange}
                        placeholder="e.g. 123456789"
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                      {validationError && (
                        <p className="text-xs text-red-400 mt-1">{validationError}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={isMutating || validationError !== null}
                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
                      >
                        {isMutating ? 'Saving...' : 'Save'}
                      </button>

                      {saveStatus === 'success' && (
                        <span className="text-sm text-green-400">Saved.</span>
                      )}
                      {saveStatus === 'error' && (
                        <span className="text-sm text-red-400">Failed to save. Please try again.</span>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
